import React, { useEffect, useMemo, useState } from "react";
import {
  Bot,
  Plus,
  Save,
  Trash2,
  RefreshCw,
  Search,
  Eye,
  EyeOff,
  BookOpen,
  Terminal,
  HelpCircle,
  Coins,
  Info,
  X,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Ban,
  Rocket,
  FileCode2,
  GitCompare,
  Activity,
  Server as ServerCog,
  Sparkles,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminServerGuide,
  adminServerGuideDiscoveries,
  fetchServers,
} from "@/lib/api";

const TYPES = [
  { value: "command", label: "Command", icon: Terminal },
  { value: "guide", label: "Guide", icon: BookOpen },
  { value: "faq", label: "FAQ", icon: HelpCircle },
  { value: "earning_method", label: "Earning Method", icon: Coins },
  { value: "info", label: "Info", icon: Info },
];

const EMPTY_FORM = {
  id: "",
  type: "guide",
  category: "general",
  title: "",
  description: "",
  content: "",
  usage: "",
  examples: [],
  keywords: [],
  related_commands: [],
  plugin: "",
  status: "draft",
  verified: false,
  sort_order: 0,
  enabled: true,
};

function typeLabel(type) {
  return TYPES.find((x) => x.value === type)?.label || type;
}

function normalizeGuide(item) {
  return {
    id: item.id || "",
    type: item.type || "guide",
    category: item.category || "general",
    title: item.title || "",
    description: item.description || "",
    content: item.content || "",
    usage: item.usage || "",
    examples: Array.isArray(item.examples) ? item.examples : [],
    keywords: Array.isArray(item.keywords) ? item.keywords : [],
    related_commands: Array.isArray(item.related_commands)
      ? item.related_commands
      : [],
    plugin: item.plugin || "",
    status: item.status || (item.verified ? "verified" : "draft"),
    verified: item.verified === true,
    sort_order: Number(item.sort_order ?? item.priority ?? 0),
    enabled: item.enabled !== false,
  };
}

function csvToArray(value) {
  return String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function arrayToCsv(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

export default function AdminDrkCEco() {
  const [servers, setServers] = useState([]);
  const [serverKey, setServerKey] = useState("");
  const [guides, setGuides] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loadingServers, setLoadingServers] = useState(true);
  const [loadingGuides, setLoadingGuides] = useState(false);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showDisabled, setShowDisabled] = useState(true);

  // Auto Discovery Review States
  const [discoveryStats, setDiscoveryStats] = useState(null);
  const [discoveries, setDiscoveries] = useState([]);
  const [discoveryFilter, setDiscoveryFilter] = useState("pending");
  const [discoverySourceFilter, setDiscoverySourceFilter] = useState("all");
  const [loadingDiscoveries, setLoadingDiscoveries] = useState(false);
  const [discoveryAction, setDiscoveryAction] = useState("");
  const [selectedDiscovery, setSelectedDiscovery] = useState(null);
  const [discoveryEdit, setDiscoveryEdit] = useState(null);
  const [savingDiscoveryEdit, setSavingDiscoveryEdit] = useState(false);

  const loadDiscoveryData = async (
    key = serverKey,
    reviewStatus = discoveryFilter,
    sourceFilter = discoverySourceFilter
  ) => {
    if (!key) {
      setDiscoveryStats(null);
      setDiscoveries([]);
      return;
    }

    try {
      setLoadingDiscoveries(true);

      const [statsData, listData] = await Promise.all([
        adminServerGuideDiscoveries.stats(key),
        adminServerGuideDiscoveries.list(key, {
          ...(reviewStatus !== "all" ? { review_status: reviewStatus } : {}),
          ...(sourceFilter !== "all" ? { source_type: sourceFilter } : {}),
          limit: 100,
        }),
      ]);

      setDiscoveryStats(statsData || null);

      const list = Array.isArray(listData)
        ? listData
        : Array.isArray(listData?.items)
        ? listData.items
        : [];

      setDiscoveries(list);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Failed to load auto discoveries"
      );
      setDiscoveries([]);
    } finally {
      setLoadingDiscoveries(false);
    }
  };

  const refreshDiscoveries = async () => {
    await loadDiscoveryData(serverKey, discoveryFilter, discoverySourceFilter);
  };

  const editPublishedGuide = (item) => {
    if (!serverKey || !item) return;

    const publishedGuideId =
      item.promoted_guide_id ||
      item.published_guide_id ||
      item.guide_id ||
      item.server_guide_id;

    let guide = null;

    if (publishedGuideId) {
      guide = guides.find((x) => String(x.id) === String(publishedGuideId));
    }

    if (!guide && item.id) {
      guide = guides.find(
        (x) =>
          String(x.discovery_id || "") === String(item.id) ||
          String(x.source_discovery_id || "") === String(item.id)
      );
    }

    if (!guide && item.title) {
      guide = guides.find((x) => {
        const sameTitle =
          String(x.title || "").trim().toLowerCase() ===
          String(item.title || "").trim().toLowerCase();

        const sameCommand =
          String(x.command || "").trim().toLowerCase() ===
          String(item.command || "").trim().toLowerCase();

        return sameTitle && (!item.command || sameCommand);
      });
    }

    if (!guide) {
      toast.error(
        "Published guide could not be matched to Server Knowledge. Try refreshing knowledge first."
      );
      return;
    }

    setSelectedId(guide.id);
    setForm(normalizeGuide(guide));
    setDiscoveryEdit(null);
    setSelectedDiscovery(null);

    requestAnimationFrame(() => {
      document
        .getElementById("server-knowledge-editor")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    toast.success("Published guide loaded into editor");
  };

  const runDiscoveryAction = async (action, discovery) => {
    if (!serverKey || !discovery?.id) return;

    try {
      setDiscoveryAction(`${action}:${discovery.id}`);

      if (action === "approve") {
        await adminServerGuideDiscoveries.approve(serverKey, discovery.id);
        toast.success("Discovery approved");
      }

      if (action === "ignore") {
        await adminServerGuideDiscoveries.ignore(serverKey, discovery.id);
        toast.success("Discovery ignored");
      }

      if (action === "promote") {
        await adminServerGuideDiscoveries.promote(serverKey, discovery.id);
        toast.success("Discovery promoted to DrkC Eco");
      }

      if (
        action === "manual_wins" ||
        action === "discovery_wins" ||
        action === "no_conflict"
      ) {
        await adminServerGuideDiscoveries.resolveConflict(
          serverKey,
          discovery.id,
          action
        );
        toast.success("Conflict resolution saved");
      }

      setSelectedDiscovery(null);
      await loadDiscoveryData(serverKey, discoveryFilter, discoverySourceFilter);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          `Failed to ${action.replaceAll("_", " ")}`
      );
    } finally {
      setDiscoveryAction("");
    }
  };

  const discoveryCounts = {
    pending: Number(discoveryStats?.pending || 0),
    approved: Number(discoveryStats?.approved || 0),
    ignored: Number(discoveryStats?.ignored || 0),
    promoted: Number(discoveryStats?.promoted || 0),
    conflicts: Number(discoveryStats?.conflicts || 0),
  };

  const loadServers = async () => {
    try {
      setLoadingServers(true);
      const data = await fetchServers();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.servers)
        ? data.servers
        : [];

      setServers(list);

      if (!serverKey && list.length) {
        setServerKey(list[0].key);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load servers");
    } finally {
      setLoadingServers(false);
    }
  };

  const loadGuides = async (key = serverKey) => {
    if (!key) {
      setGuides([]);
      return;
    }

    try {
      setLoadingGuides(true);
      const data = await adminServerGuide.list(key);
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : [];

      setGuides(list.map(normalizeGuide));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load DrkC Eco knowledge");
      setGuides([]);
    } finally {
      setLoadingGuides(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  useEffect(() => {
    if (serverKey) {
      loadGuides(serverKey);
      loadDiscoveryData(serverKey, discoveryFilter, discoverySourceFilter);
    }
  }, [serverKey]);

  useEffect(() => {
    if (serverKey) {
      loadDiscoveryData(serverKey, discoveryFilter, discoverySourceFilter);
    }
  }, [discoveryFilter, discoverySourceFilter]);

  const filteredGuides = useMemo(() => {
    const q = query.trim().toLowerCase();

    return guides.filter((item) => {
      if (!showDisabled && !item.enabled) return false;
      if (filterType !== "all" && item.type !== filterType) return false;

      if (!q) return true;

      const haystack = [
        item.title,
        item.description,
        item.content,
        item.category,
        ...(item.keywords || []),
        ...(item.related_commands || []),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [guides, query, filterType, showDisabled]);

  const stats = useMemo(() => {
    return {
      total: guides.length,
      verified: guides.filter(
        (x) => x.verified === true && x.status === "verified"
      ).length,
      draft: guides.filter((x) => x.status !== "verified" && x.enabled).length,
      disabled: guides.filter((x) => !x.enabled).length,
      commands: guides.filter((x) => x.type === "command").length,
      guides: guides.filter((x) => x.type === "guide").length,
      faqs: guides.filter((x) => x.type === "faq").length,
      earnings: guides.filter((x) => x.type === "earning_method").length,
    };
  }, [guides]);

  const startNew = () => {
    setSelectedId(null);
    setForm(EMPTY_FORM);
    document
      .getElementById("server-knowledge-editor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const editGuide = (item) => {
    setSelectedId(item.id);
    setForm(normalizeGuide(item));
    document
      .getElementById("server-knowledge-editor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveGuide = async () => {
    if (!serverKey) {
      toast.error("Select a server first");
      return;
    }

    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category.trim() || "general",
        content: form.content.trim(),
        usage: form.usage.trim(),
        examples: Array.isArray(form.examples) ? form.examples : [],
        keywords: Array.isArray(form.keywords) ? form.keywords : [],
        related_commands: Array.isArray(form.related_commands)
          ? form.related_commands
          : [],
        plugin: String(form.plugin || "").trim(),
        status: form.verified ? "verified" : "draft",
        verified: Boolean(form.verified),
        sort_order: Number(form.sort_order || 0),
        enabled: Boolean(form.enabled),
      };

      delete payload.id;

      if (selectedId) {
        await adminServerGuide.update(serverKey, selectedId, payload);
        toast.success("DrkC Eco knowledge updated");
      } else {
        await adminServerGuide.create(serverKey, payload);
        toast.success("DrkC Eco knowledge created");
      }

      startNew();
      await loadGuides(serverKey);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Failed to save DrkC Eco knowledge"
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteGuide = async (item) => {
    if (!serverKey || !item?.id) return;

    if (!window.confirm(`Delete "${item.title}"?`)) return;

    try {
      await adminServerGuide.remove(serverKey, item.id);
      toast.success("Knowledge deleted");

      if (selectedId === item.id) {
        startNew();
      }

      await loadGuides(serverKey);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to delete knowledge");
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-[#07070b] text-white">
      <div className="mx-auto max-w-[1800px] space-y-6 p-4 md:p-6 lg:p-8">
        
        {/* ======================================================
            DRKC ECO — HEADER & CONTEXT BAR
        ======================================================= */}
        <header className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#151324] via-[#0d0d14] to-[#08080c] p-6 shadow-2xl">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#9387FE]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-[#FFBF00]/5 blur-3xl" />

          <div className="relative flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#9387FE]/30 bg-[#9387FE]/10 shadow-lg shadow-[#9387FE]/10">
                <Bot size={28} className="text-[#B4AEF4]" />
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[#9387FE]/20 bg-[#9387FE]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#B4AEF4]">
                    DrkC Eco
                  </span>

                  <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/10 bg-emerald-400/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-300">
                    <Activity size={11} />
                    Admin Workspace
                  </span>
                </div>

                <h1 className="mt-3 text-2xl font-black tracking-tight md:text-3xl">
                  Knowledge Intelligence Center
                </h1>

                <p className="mt-1 max-w-2xl text-sm leading-6 text-white/45">
                  Manage verified server knowledge, imported discoveries, and documentation from one place.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/40 p-2 pl-4">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/40">
                  <ServerCog size={14} className="text-[#B4AEF4]" />
                  Server:
                </div>
                <select
                  value={serverKey}
                  onChange={(e) => setServerKey(e.target.value)}
                  disabled={loadingServers}
                  className="rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-sm font-semibold outline-none transition focus:border-[#9387FE]"
                >
                  <option value="">
                    {loadingServers ? "Loading..." : "Select server"}
                  </option>
                  {servers.map((server) => (
                    <option key={server.key} value={server.key}>
                      {server.name || server.key}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => {
                  loadGuides(serverKey);
                  loadDiscoveryData(serverKey, discoveryFilter, discoverySourceFilter);
                }}
                disabled={!serverKey || loadingGuides || loadingDiscoveries}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold transition hover:bg-white/[0.08] disabled:opacity-40"
              >
                <RefreshCw
                  size={16}
                  className={loadingGuides || loadingDiscoveries ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <button
                type="button"
                onClick={startNew}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFBF00] px-5 py-3 text-sm font-bold text-black transition hover:brightness-110"
              >
                <Plus size={18} />
                New Document
              </button>
            </div>
          </div>
        </header>

        {/* ============================================================
            AUTO DISCOVERY REVIEW CENTER
        ============================================================ */}
        <section className="rounded-3xl border border-[#9387FE]/20 bg-gradient-to-br from-[#11101d] via-[#0b0a12] to-[#08080c] p-6 shadow-xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-[#9387FE]/15 p-3 text-[#B4AEF4]">
                <Sparkles size={24} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold tracking-tight">Auto Discovery Review</h2>
                  {discoveryCounts.pending > 0 && (
                    <span className="rounded-full bg-[#FFBF00]/15 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#FFBF00]">
                      {discoveryCounts.pending} Pending
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-white/45">
                  Review automatically parsed commands and plugin configs before publishing.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={refreshDiscoveries}
              disabled={loadingDiscoveries || !serverKey}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-xs font-semibold hover:bg-white/10 disabled:opacity-40"
            >
              <RefreshCw size={14} className={loadingDiscoveries ? "animate-spin" : ""} />
              Sync Discoveries
            </button>
          </div>

          {/* Metric Selector Cards */}
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              { key: "pending", label: "Pending", value: discoveryCounts.pending, icon: Bot, cls: "text-[#FFBF00]" },
              { key: "conflict", label: "Conflicts", value: discoveryCounts.conflicts, icon: AlertTriangle, cls: "text-red-400" },
              { key: "approved", label: "Approved", value: discoveryCounts.approved, icon: CheckCircle2, cls: "text-emerald-400" },
              { key: "ignored", label: "Ignored", value: discoveryCounts.ignored, icon: Ban, cls: "text-white/40" },
              { key: "promoted", label: "Promoted", value: discoveryCounts.promoted, icon: Rocket, cls: "text-[#B4AEF4]" },
            ].map((item) => {
              const Icon = item.icon;
              const active = discoveryFilter === item.key || (item.key === "conflict" && discoveryFilter === "all");

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    if (item.key === "conflict") {
                      setDiscoveryFilter("all");
                      setDiscoverySourceFilter("all");
                    } else {
                      setDiscoveryFilter(item.key);
                    }
                  }}
                  className={`rounded-2xl border p-4 text-left transition ${
                    active
                      ? "border-[#9387FE]/50 bg-[#9387FE]/10"
                      : "border-white/10 bg-black/20 hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                      {item.label}
                    </span>
                    <Icon size={16} className={item.cls} />
                  </div>
                  <div className="mt-2 text-2xl font-black">{item.value}</div>
                </button>
              );
            })}
          </div>

          {/* Filtering controls */}
          <div className="mt-5 flex flex-col gap-3 md:flex-row">
            <select
              value={discoveryFilter}
              onChange={(e) => setDiscoveryFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-xs font-medium outline-none focus:border-[#9387FE]"
            >
              <option value="all">All Review States</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="ignored">Ignored</option>
              <option value="promoted">Promoted</option>
            </select>

            <select
              value={discoverySourceFilter}
              onChange={(e) => setDiscoverySourceFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-xs font-medium outline-none focus:border-[#9387FE]"
            >
              <option value="all">All Sources</option>
              <option value="auto">Auto</option>
              <option value="config">Config</option>
            </select>

            <div className="flex-1 rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-xs text-white/40">
              <span className="font-semibold text-white/60">Note:</span> Explicitly defined manuals take precedence over discovered configuration defaults.
            </div>
          </div>

          {/* Discovery Items List */}
          <div className="mt-5 space-y-3">
            {loadingDiscoveries ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 p-8 text-center text-sm text-white/40">
                <RefreshCw size={20} className="mx-auto animate-spin text-[#B4AEF4]" />
                <p className="mt-3">Loading discovered records...</p>
              </div>
            ) : discoveries.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-8 text-center">
                <ShieldCheck size={28} className="mx-auto text-emerald-400/50" />
                <p className="mt-3 font-semibold">No discoveries found</p>
                <p className="mt-1 text-xs text-white/35">Adjust filter options to inspect other categories.</p>
              </div>
            ) : (
              discoveries.map((item) => {
                const conflict = Boolean(item.conflict_detected);
                const busy = discoveryAction.endsWith(`:${item.id}`);

                return (
                  <article
                    key={item.id}
                    className={`rounded-2xl border p-4 transition ${
                      conflict
                        ? "border-red-400/30 bg-red-400/[0.03]"
                        : "border-white/10 bg-black/30 hover:bg-black/40"
                    }`}
                  >
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#9387FE]/15 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-[#B4AEF4]">
                            {item.type || "info"}
                          </span>
                          <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[9px] uppercase tracking-widest text-white/45">
                            {item.source_type || "auto"}
                          </span>
                          {item.source_plugin && (
                            <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[9px] uppercase tracking-widest text-white/45">
                              {item.source_plugin}
                            </span>
                          )}
                          {conflict && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-400/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-red-300">
                              <AlertTriangle size={11} />
                              Conflict
                            </span>
                          )}
                        </div>

                        <h3 className="mt-2 text-base font-bold">{item.title || "Untitled discovery"}</h3>

                        {item.command && (
                          <code className="mt-1.5 inline-block rounded-md bg-black/60 px-2.5 py-1 font-mono text-xs text-[#B4AEF4]">
                            {item.command}
                          </code>
                        )}

                        {item.description && (
                          <p className="mt-2 text-xs leading-5 text-white/60">{item.description}</p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 xl:max-w-[420px] xl:justify-end">
                        {item.review_status === "promoted" && (
                          <button
                            type="button"
                            onClick={() => editPublishedGuide(item)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#FFBF00]/10 px-3 py-1.5 text-xs font-semibold text-[#FFBF00] hover:bg-[#FFBF00]/20"
                          >
                            <FileCode2 size={14} />
                            Edit Published Guide
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => setSelectedDiscovery(selectedDiscovery?.id === item.id ? null : item)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium hover:bg-white/5"
                        >
                          <FileCode2 size={14} />
                          Details
                        </button>

                        {item.review_status === "pending" && !conflict && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runDiscoveryAction("approve", item)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40"
                          >
                            <CheckCircle2 size={14} />
                            Approve
                          </button>
                        )}

                        {(item.review_status === "pending" || item.review_status === "approved") && !conflict && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runDiscoveryAction("promote", item)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#9387FE]/15 px-3 py-1.5 text-xs font-semibold text-[#B4AEF4] hover:bg-[#9387FE]/25 disabled:opacity-40"
                          >
                            <Rocket size={14} />
                            Promote
                          </button>
                        )}

                        {item.review_status !== "ignored" && item.review_status !== "promoted" && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm("Ignore this auto discovery?")) {
                                runDiscoveryAction("ignore", item);
                              }
                            }}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white/50 hover:bg-white/5 disabled:opacity-40"
                          >
                            <Ban size={14} />
                            Ignore
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Extended Details Panel */}
                    {selectedDiscovery?.id === item.id && (
                      <div className="mt-4 border-t border-white/10 pt-4">
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="rounded-lg bg-black/40 p-3">
                            <div className="text-[9px] uppercase tracking-widest text-white/30">Source Path</div>
                            <div className="mt-1 break-all font-mono text-xs text-white/65">{item.source_path || "Unknown"}</div>
                          </div>
                          <div className="rounded-lg bg-black/40 p-3">
                            <div className="text-[9px] uppercase tracking-widest text-white/30">Plugin</div>
                            <div className="mt-1 text-xs text-white/65">{item.source_plugin || "Unknown"}</div>
                          </div>
                        </div>

                        {item.content && (
                          <div className="mt-3 rounded-lg bg-black/50 p-4">
                            <div className="mb-2 text-[9px] uppercase tracking-widest text-white/30">Discovered Content</div>
                            <pre className="max-h-60 overflow-auto whitespace-pre-wrap text-xs font-mono leading-5 text-white/60">{item.content}</pre>
                          </div>
                        )}

                        {conflict && (
                          <div className="mt-4 rounded-xl border border-red-400/20 bg-red-400/[0.04] p-4">
                            <div className="flex items-center gap-2 text-xs font-semibold text-red-300">
                              <GitCompare size={15} />
                              Conflict Detected
                            </div>
                            <p className="mt-1 text-xs leading-5 text-white/45">
                              This discovery conflicts with an existing document. Choose a resolution strategy below.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => runDiscoveryAction("manual_wins", item)}
                                className="rounded-lg border border-white/10 px-3 py-1.5 text-xs hover:bg-white/5"
                              >
                                Preserve Manual Record
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => runDiscoveryAction("discovery_wins", item)}
                                className="rounded-lg bg-red-400/10 px-3 py-1.5 text-xs text-red-300 hover:bg-red-400/20"
                              >
                                Overwrite With Discovery
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => runDiscoveryAction("no_conflict", item)}
                                className="rounded-lg border border-white/10 px-3 py-1.5 text-xs hover:bg-white/5"
                              >
                                Dismiss Conflict Flag
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </section>

        {/* ============================================================
            DATABASE KNOWLEDGE STATS
        ============================================================ */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
          {[
            ["Total", stats.total],
            ["Verified", stats.verified],
            ["Drafts", stats.draft],
            ["Disabled", stats.disabled],
            ["Commands", stats.commands],
            ["Guides", stats.guides],
            ["FAQs", stats.faqs],
            ["Earnings", stats.earnings],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">{label}</p>
              <p className="mt-2 text-2xl font-black">{value}</p>
            </div>
          ))}
        </div>

        {/* ============================================================
            EDITOR PANEL
        ============================================================ */}
        <div
          id="server-knowledge-editor"
          className="rounded-3xl border border-[#9387FE]/20 bg-white/[0.02] p-6 shadow-xl"
        >
          <div className="mb-6 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest text-[#B4AEF4]">
                  {selectedId ? "Edit Knowledge Document" : "Create New Knowledge"}
                </span>
                {selectedId && (
                  <span className="rounded-full bg-emerald-400/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300">
                    Active Record ID: {selectedId}
                  </span>
                )}
              </div>
              <h2 className="mt-1 text-xl font-bold">
                {selectedId ? form.title || "Untitled Record" : "New DrkC Eco Entry"}
              </h2>
            </div>

            {selectedId && (
              <button
                type="button"
                onClick={startNew}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3.5 py-2 text-xs font-semibold text-white/60 hover:bg-white/5 hover:text-white"
              >
                <X size={16} />
                Cancel Editing
              </button>
            )}
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Type
              </span>
              <select
                value={form.type}
                onChange={(e) => updateField("type", e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              >
                {TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Category
              </span>
              <input
                value={form.category}
                onChange={(e) => updateField("category", e.target.value)}
                placeholder="general, economy, claims, etc."
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Title
              </span>
              <input
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="e.g., How to create a land claim"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Short Description
              </span>
              <textarea
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                rows={2}
                placeholder="Brief summary used in search indexing and fast replies..."
                className="w-full resize-y rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Full Article / Markdown Content
              </span>
              <textarea
                value={form.content}
                onChange={(e) => updateField("content", e.target.value)}
                rows={6}
                placeholder="Detailed explanations, step-by-step instructions, or markdown content..."
                className="w-full resize-y rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Command Usage Pattern
              </span>
              <input
                value={form.usage}
                onChange={(e) => updateField("usage", e.target.value)}
                placeholder="/claim create [name]"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Keywords (Comma Separated)
              </span>
              <input
                value={arrayToCsv(form.keywords)}
                onChange={(e) => updateField("keywords", csvToArray(e.target.value))}
                placeholder="land, claim, protection, grief"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Related Commands (Comma Separated)
              </span>
              <input
                value={arrayToCsv(form.related_commands)}
                onChange={(e) => updateField("related_commands", csvToArray(e.target.value))}
                placeholder="/trust, /untrust, /claimlist"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Source Plugin Name
              </span>
              <input
                value={form.plugin}
                onChange={(e) => updateField("plugin", e.target.value)}
                placeholder="GriefPrevention, Essentials, etc."
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Verification Status
              </span>
              <select
                value={form.status}
                onChange={(e) => {
                  const status = e.target.value;
                  updateField("status", status);
                  updateField("verified", status === "verified");
                }}
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              >
                <option value="draft">🟡 Draft — Internal Review</option>
                <option value="verified">🟢 Verified — Live for AI Bot</option>
                <option value="disabled">🔴 Disabled — Inactive</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-widest text-white/40">
                Sort Priority Order
              </span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => updateField("sort_order", e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm outline-none transition focus:border-[#9387FE]"
              />
            </label>

            <div className="flex items-end">
              <label className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-black/30 p-3.5">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => updateField("enabled", e.target.checked)}
                  className="h-4 w-4 rounded accent-[#9387FE]"
                />
                <div>
                  <span className="block text-xs font-bold text-white">Enable Knowledge Entry</span>
                  <span className="block text-[11px] text-white/40">Allow AI features to index this record</span>
                </div>
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-white/10 pt-5">
            <button
              type="button"
              onClick={startNew}
              className="rounded-xl border border-white/10 px-5 py-2.5 text-xs font-semibold hover:bg-white/5"
            >
              Reset Form
            </button>

            <button
              type="button"
              onClick={saveGuide}
              disabled={saving || !serverKey}
              className="inline-flex items-center gap-2 rounded-xl bg-[#FFBF00] px-6 py-2.5 text-xs font-bold text-black transition hover:brightness-110 disabled:opacity-40"
            >
              <Save size={16} />
              {saving ? "Saving Changes..." : selectedId ? "Update Record" : "Create Knowledge"}
            </button>
          </div>
        </div>

        {/* ============================================================
            KNOWLEDGE DATABASE BROWSER
        ============================================================ */}
        <section className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search active knowledge base..."
                className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-10 pr-4 text-xs outline-none focus:border-[#9387FE]"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs outline-none focus:border-[#9387FE]"
              >
                <option value="all">All Content Types</option>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => setShowDisabled((v) => !v)}
                className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2 text-xs font-medium hover:bg-white/5"
              >
                {showDisabled ? <Eye size={15} /> : <EyeOff size={15} />}
                {showDisabled ? "Showing Hidden" : "Active Only"}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {loadingGuides ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.01] p-12 text-center text-sm text-white/40">
                <RefreshCw size={24} className="mx-auto animate-spin text-[#B4AEF4]" />
                <p className="mt-3">Loading server knowledge...</p>
              </div>
            ) : filteredGuides.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-white/10 bg-white/[0.01] p-12 text-center">
                <Layers size={32} className="mx-auto text-white/20" />
                <p className="mt-3 font-semibold">No records match your filters</p>
                <p className="mt-1 text-xs text-white/40">Create a entry above or reset your search term.</p>
              </div>
            ) : (
              filteredGuides.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl border border-white/10 bg-black/20 p-5 transition hover:border-white/20 hover:bg-black/30"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#9387FE]/15 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#B4AEF4]">
                          {typeLabel(item.type)}
                        </span>

                        <span className="rounded-full bg-white/5 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-white/40">
                          {item.category}
                        </span>

                        {item.status === "verified" && item.verified ? (
                          <span className="rounded-full bg-emerald-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                            ✓ Verified
                          </span>
                        ) : item.status === "disabled" ? (
                          <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-300">
                            ✕ Disabled
                          </span>
                        ) : (
                          <span className="rounded-full bg-yellow-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-yellow-300">
                            ! Draft
                          </span>
                        )}

                        {item.plugin && (
                          <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-blue-300">
                            🔌 {item.plugin}
                          </span>
                        )}
                      </div>

                      <h3 className="mt-3 text-base font-bold">{item.title}</h3>
                      <p className="mt-1 text-xs leading-5 text-white/60">{item.description}</p>

                      {item.usage && (
                        <code className="mt-3 inline-block rounded-lg bg-black/60 px-3 py-1.5 font-mono text-xs text-[#B4AEF4]">
                          {item.usage}
                        </code>
                      )}

                      {item.keywords?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {item.keywords.map((kw) => (
                            <span key={kw} className="rounded bg-white/5 px-2 py-0.5 text-[10px] text-white/40">
                              #{kw}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => editGuide(item)}
                        className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2 text-xs font-semibold hover:bg-white/10"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteGuide(item)}
                        className="rounded-xl border border-red-500/20 bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                        title="Delete record"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

      </div>
    </div>
  );
}